import React, { useEffect, useRef, useState } from "react";
import { askSean, getChatHistory } from "@/lib/api";
import Modal from "./Modal";

export default function AskSeanModal({ open, onClose, sessionId }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open && sessionId) getChatHistory(sessionId).then(setMessages).catch(() => setMessages([]));
  }, [open, sessionId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setLoading(true);
    try {
      const res = await askSean(sessionId, text);
      setMessages((m) => [...m, { role: "assistant", content: res.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Signal lost — try again, scientist!" }]);
    } finally { setLoading(false); }
  };

  const suggested = [
    "Why does my heart beat faster when I'm scared?",
    "How do black holes work?",
    "What's the science of laughter?",
    "Why is reading good for my brain?",
  ];

  return (
    <Modal open={open} onClose={onClose} title="💬 Ask Sean" testId="ask-sean-modal" wide tint="brain">
      <p className="text-[var(--ink-soft)] text-sm mb-4">
        Sean answers in his voice — <strong className="text-[var(--ink)]">credible · clear · concise</strong>.
      </p>
      <div ref={scrollRef} className="space-y-3 max-h-[55vh] overflow-y-auto pr-1 mb-4" data-testid="chat-history">
        {messages.length === 0 && (
          <div className="space-y-2">
            <div className="label-tag">// try one of these:</div>
            {suggested.map((q, i) => (
              <button
                key={i}
                onClick={() => setInput(q)}
                className="block w-full text-left text-sm px-4 py-3 rounded-xl card-flat hover:border-[var(--cyan)] hover:translate-x-1 transition text-[var(--ink)]"
                data-testid={`suggested-q-${i}`}
              >
                💭 {q}
              </button>
            ))}
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className="max-w-[82%] rounded-2xl px-4 py-3" style={
              m.role === "user"
                ? { background: "linear-gradient(135deg,#00e5ff,#0093ff)", color: "#04050d" }
                : { background: "rgba(255,46,147,0.12)", border: "1px solid rgba(255,46,147,0.32)", color: "#fff" }
            }>
              {m.role === "assistant" && <div className="eyebrow text-[var(--brain-soft)] mb-1">// SEAN 🧠🫀</div>}
              <div className="whitespace-pre-wrap text-sm leading-relaxed">{m.content}</div>
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="rounded-2xl px-4 py-3" style={{ background: "rgba(255,46,147,0.12)", border: "1px solid rgba(255,46,147,0.32)" }}>
              <div className="flex gap-1.5">
                <span className="w-2 h-2 rounded-full bg-[var(--brain)] animate-pulse" />
                <span className="w-2 h-2 rounded-full bg-[var(--brain)] animate-pulse" style={{ animationDelay: "0.15s" }} />
                <span className="w-2 h-2 rounded-full bg-[var(--brain)] animate-pulse" style={{ animationDelay: "0.3s" }} />
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="flex gap-2">
        <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Ask Sean a science question..." className="field flex-1" data-testid="chat-input" disabled={loading} />
        <button onClick={send} disabled={loading || !input.trim()} className="btn btn-primary" data-testid="chat-send">{loading ? "..." : "Send"}</button>
      </div>
    </Modal>
  );
}
